import { useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Typography,
  Badge,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Stack,
  Markdown,
  Button,
  toast,
} from "@altimateai/lego";
import { IconThumbUp, IconThumbDown, IconCheck } from "@tabler/icons-react";
import { submitKnowledgeFeedback } from "@/helpers/apis";
import { KnowledgeTracking } from "@/lib/tracking/knowledge";
import { Learning } from "./types";

interface OutletContext {
  learnings: Learning[];
}

export default function LearningDetailsSidebar() {
  const { id, learningId } = useParams();
  const navigate = useNavigate();
  const { learnings } = useOutletContext<OutletContext>();
  const [submittedFeedback, setSubmittedFeedback] = useState<"helpful" | "not_helpful" | null>(null);

  // Find the selected learning from the learnings array
  const learning = learningId ? learnings.find(l => l.id === Number(learningId)) || null : null;
  const isOpen = !!learningId && !!learning;

  const feedbackMutation = useMutation({
    mutationFn: (feedback: "helpful" | "not_helpful") =>
      submitKnowledgeFeedback(Number(id), learning?.id || 0, feedback),
    onSuccess: (_, feedback) => {
      KnowledgeTracking.learningFeedbackSubmitted(
        Number(id),
        learning?.id || 0,
        feedback,
      );
      setSubmittedFeedback(feedback);
      toast({
        title: "Thanks for your feedback!",
        description: feedback === "helpful"
          ? "We're glad this learning was useful."
          : "We'll work on improving this.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to submit feedback",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setSubmittedFeedback(null);
    navigate(`/knowledge-hub/${id}/learnings`);
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && handleClose()}>
      <SheetContent side="right" className="al-w-full al-min-w-[600px] sm:al-max-w-[700px] al-p-0">
        <Stack direction="column" className="al-h-full">
          <SheetHeader>
            <SheetTitle>
              <Typography variant="h3" weight="semibold">
                Learning Details
              </Typography>
            </SheetTitle>
          </SheetHeader>
          <div className="al-overflow-y-auto " style={{ flex: 1 }}>
            {learning && (
              <Stack direction="column" gap="md">
                {/* Chunk Information Card */}
                <div className="al-border al-border-gray-200 al-rounded-lg al-p-4 al-bg-gray-50">
                  <Stack direction="column" gap="md">
                    {/* Title */}
                    <Stack direction="column" gap="xs">
                      <Typography
                        variant="body"
                        weight="medium"
                        className="al-text-gray-600 al-uppercase al-tracking-wide"
                      >
                        Learning Name
                      </Typography>
                      <Typography variant="h4" weight="semibold" className="al-text-gray-900">
                        {learning.name}
                      </Typography>
                    </Stack>

                    {/* Summary */}
                    {learning.description && (
                      <Stack direction="column" gap="xs">
                        <Typography
                          variant="body"
                          weight="medium"
                          className="al-text-gray-600 al-uppercase al-tracking-wide"
                        >
                          Summary
                        </Typography>
                        <Typography
                          variant="body"
                          className="al-text-gray-700 al-leading-relaxed"
                        >
                          {learning.description}
                        </Typography>
                      </Stack>
                    )}

                    <div className="al-border-t al-border-gray-200 al-pt-3 al-mt-1" />

                    {/* Metadata Grid - Using semantic HTML for accessibility */}
                    <dl className="al-grid al-grid-cols-2 al-gap-4">
                      {/* Knowledge Type */}
                      {learning.knowledge_type && (
                        <Stack direction="column" gap="xs">
                          <dt id="knowledge-type-label">
                            <Typography
                              variant="body"
                              weight="medium"
                              className="al-text-gray-600"
                            >
                              Type
                            </Typography>
                          </dt>
                          <dd aria-labelledby="knowledge-type-label">
                            <Badge
                              variant="outline"
                              className="al-bg-blue-50 al-text-blue-600 al-border-blue-200 al-text-xs al-capitalize al-w-fit"
                            >
                              {learning.knowledge_type}
                            </Badge>
                          </dd>
                        </Stack>
                      )}

                      {/* Retrievals */}
                      <Stack direction="column" gap="xs">
                        <dt id="retrievals-label">
                          <Typography
                            variant="body"
                            weight="medium"
                            className="al-text-gray-600"
                          >
                            Retrieved
                          </Typography>
                        </dt>
                        <dd aria-labelledby="retrievals-label">
                          <Typography
                            variant="body"
                            weight="medium"
                            className="al-text-gray-900"
                          >
                            {learning.retrievals ?? 0} times
                          </Typography>
                        </dd>
                      </Stack>
                    </dl>

                    {/* Tags */}
                    {learning.tags && learning.tags.length > 0 && (
                      <>
                        <div className="al-border-t al-border-gray-200 al-pt-3 al-mt-1" />
                        <Stack direction="column" gap="sm">
                          <Typography
                            variant="body"
                            weight="medium"
                            className="al-text-gray-600 al-uppercase al-tracking-wide"
                          >
                            Tags
                          </Typography>
                          <Stack direction="row" gap="sm" wrap>
                            {learning.tags.map((tag, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="al-bg-gray-50 al-text-gray-700 al-border-gray-200 al-text-xs al-font-normal al-px-2.5 al-py-1"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </Stack>
                        </Stack>
                      </>
                    )}
                  </Stack>
                </div>

                {/* Content Section */}
                <Stack direction="column" gap="sm">
                  <Typography variant="h4" weight="semibold" className="al-text-gray-900">
                    Content
                  </Typography>
                  <div className="al-border al-border-gray-200 al-rounded-lg al-p-6 al-bg-white">
                    <div className="al-prose al-prose-sm al-max-w-none al-text-gray-700 markdown-content">
                      <Markdown content={learning.content} />
                    </div>
                  </div>
                </Stack>

                {/* Feedback Section */}
                <div className="al-border-t al-border-gray-200 al-pt-4 al-mt-2">
                  <Stack direction="row" align="center" justify="between">
                    <Typography variant="body" size="sm" className="al-text-gray-600">
                      Was this learning helpful?
                    </Typography>
                    {submittedFeedback ? (
                      <Stack direction="row" align="center" gap="xs" className="al-text-green-600">
                        <IconCheck className="al-w-4 al-h-4" />
                        <Typography variant="body" size="sm" weight="medium">
                          Thanks for your feedback
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" gap="sm">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => feedbackMutation.mutate("helpful")}
                          disabled={feedbackMutation.isPending}
                          className="al-gap-1"
                        >
                          <IconThumbUp className="al-w-4 al-h-4" />
                          Yes
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => feedbackMutation.mutate("not_helpful")}
                          disabled={feedbackMutation.isPending}
                          className="al-gap-1"
                        >
                          <IconThumbDown className="al-w-4 al-h-4" />
                          No
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </div>
              </Stack>
            )}
          </div>
        </Stack>
      </SheetContent>
    </Sheet>
  );
}
